<template>
  <div class="entra-page">
    <EntraPageHeader
      title="Authentication Methods"
      :tenantDisplayName="tenantName"
      :primaryDomain="primaryDomain"
      :regionCode="regionCode"
      :tenantStatus="tenantStatus"
      :freshnessState="freshnessState"
      :lastCollection="lastCollection"
      :dataCompleteness="dataCompleteness"
      :capabilities="capabilities"
      :showSearch="false"
      :showExport="false"
      @refresh="loadData"
    />

    <div class="page-body">
      <div v-if="error" class="error-message">{{ error }}</div>

      <div class="kpi-grid">
        <MetricCard label="Total Users" :value="totals.totalUsers" :state="summaryState" />
        <MetricCard label="MFA Registered" :value="totals.mfaRegistered" :state="summaryState" />
        <MetricCard label="MFA Capable" :value="totals.mfaCapable" :state="summaryState" />
        <MetricCard label="MFA Coverage" :value="mfaCoverage" :state="summaryState" />
        <MetricCard label="SSPR Registered" :value="totals.ssprRegistered" :state="summaryState" />
        <MetricCard label="SSPR Capable" :value="totals.ssprCapable" :state="summaryState" />
      </div>

      <div class="panels-row">
        <div class="panel-card panel-methods">
          <h3>Tenant Policy</h3>
          <p class="panel-subtitle">Methods enabled or disabled at tenant level</p>
          <div v-if="policy.length === 0" class="panel-empty">No policy data available.</div>
          <div v-for="m in policy" :key="m.methodKey" class="method-row">
            <span class="method-name">{{ formatMethodName(m.methodKey) }}</span>
            <span class="state-badge" :class="m.state === 'enabled' ? 'state-on' : 'state-off'">
              {{ m.state }}
            </span>
          </div>
        </div>

        <div class="panel-card panel-users">
          <h3>User Registration</h3>
          <p class="panel-subtitle">Per-user MFA and SSPR registration status</p>
          <div v-if="tableState === 'loading'" class="loading-state">Loading...</div>
          <div v-else class="user-list">
            <div v-for="user in users" :key="user.userId" class="user-row">
              <div class="user-info">
                <span class="user-name">{{ user.userPrincipalName }}</span>
              </div>
              <div class="user-badges">
                <span class="reg-badge" :class="user.isMfaRegistered ? 'reg-ok' : 'reg-no'">
                  {{ user.isMfaRegistered ? 'MFA' : 'No MFA' }}
                </span>
                <span v-if="user.defaultMethod" class="reg-badge reg-default">{{ formatMethodName(user.defaultMethod) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <h2 class="section-title">Methods in Use</h2>
      <div class="panel-card panel-methods-wide">
        <div v-if="summary.length === 0" class="panel-empty">No usage data available.</div>
        <div v-for="method in summary" :key="method.methodKey" class="method-row">
          <span class="method-name">{{ formatMethodName(method.methodKey) }}</span>
          <div class="method-bar-container">
            <div class="method-bar" :style="{ width: barWidth(method.userCount) + '%' }"></div>
          </div>
          <span class="method-count">{{ method.userCount }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import EntraPageHeader from '@/components/entra/common/EntraPageHeader.vue';
import MetricCard from '@/components/entra/common/MetricCard.vue';
import { getEntraAuthenticationMethods, getEntraTenantSummary, getEntraTenantCapabilities } from '@/services/entra-api.js';
import { getCapabilityStateLabel } from '@/services/entra-utils.js';

var METHOD_LABELS = {
  microsoftAuthenticatorPush: 'Microsoft Authenticator',
  microsoftAuthenticatorPasswordless: 'Authenticator (Passwordless)',
  softwareOneTimePasscode: 'Software OTP',
  hardwareOneTimePasscode: 'Hardware Token',
  windowsHelloForBusiness: 'Windows Hello',
  fido2SecurityKey: 'FIDO2 Security Key',
  temporaryAccessPass: 'TAP',
  email: 'Email OTP',
  sms: 'SMS',
  mobilePhone: 'Phone Call',
  password: 'Password',
  securityQuestion: 'Security Questions'
};

export default {
  name: 'EntraAuthenticationMethods',
  components: { EntraPageHeader, MetricCard },
  data: function() {
    return {
      tenantId: '', tenantName: '', primaryDomain: '', regionCode: '', tenantStatus: '',
      freshnessState: 'fresh', lastCollection: '', dataCompleteness: null, capabilities: [],
      totals: { totalUsers: 0, mfaRegistered: 0, mfaCapable: 0, ssprRegistered: 0, ssprCapable: 0 },
      summaryState: 'loading',
      users: [], tableState: 'loading',
      summary: [],
      policy: [],
      error: ''
    };
  },
  computed: {
    mfaCoverage: function() {
      if (this.totals.totalUsers === 0) return '0%';
      return Math.round((this.totals.mfaRegistered / this.totals.totalUsers) * 100) + '%';
    }
  },
  methods: {
    formatMethodName: function(key) {
      return METHOD_LABELS[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, function(s) { return s.toUpperCase(); });
    },
    barWidth: function(count) {
      if (this.totals.totalUsers === 0) return 0;
      return Math.round((count / this.totals.totalUsers) * 100);
    },
    loadData: function() {
      var self = this;
      this.tenantId = this.$route.params.tenantId || '';
      if (!this.tenantId) {
        this.error = 'Please select a tenant from the Portfolio page first.';
        this.tableState = 'empty';
        return;
      }

      getEntraTenantSummary(this.tenantId).then(function(data) {
        var t = data.tenant || data;
        self.tenantName = t.displayName || '';
        self.primaryDomain = t.primaryDomain || '';
        self.regionCode = t.regionCode || '';
        self.tenantStatus = t.status || '';
        self.lastCollection = t.lastSuccessfulCollectionAt || '';
        self.dataCompleteness = t.dataCompleteness || null;
      }).catch(function() {});

      getEntraTenantCapabilities(this.tenantId).then(function(data) {
        self.capabilities = (data || []).map(function(c) {
          return { key: c.capabilityKey, state: c.state, stateLabel: getCapabilityStateLabel(c.state) };
        });
      }).catch(function() {});

      getEntraAuthenticationMethods(this.tenantId).then(function(data) {
        self.users = data.users || [];
        self.summary = data.summary || [];
        self.policy = data.policy || [];
        self.totals = data.totals || { totalUsers: 0, mfaRegistered: 0, mfaCapable: 0, ssprRegistered: 0, ssprCapable: 0 };
        self.tableState = self.users.length === 0 ? 'empty' : 'loaded';
        self.summaryState = 'loaded';
      }).catch(function(err) { self.error = err.message; self.tableState = 'error'; });
    }
  },
  mounted: function() { this.loadData(); },
  watch: { '$route.params.tenantId': 'loadData' }
};
</script>

<style scoped>
.entra-page { min-height: 100%; }
.page-body { padding: 24px; }

.error-message { padding: 16px; background: #fef2f2; border: 1px solid #fecaca; border-radius: 8px; color: #991b1b; font-size: 0.875rem; margin-bottom: 16px; }

.kpi-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(170px, 1fr)); gap: 12px; margin-bottom: 24px; }

.panels-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }

.panel-card { background: #ffffff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 20px; }
.panel-card h3 { font-size: 0.875rem; font-weight: 600; color: #475569; margin: 0 0 14px 0; }

.panel-empty { font-size: 0.8125rem; color: #94a3b8; }

.method-row { display: flex; align-items: center; gap: 10px; margin-bottom: 10px; }
.method-name { font-size: 0.75rem; color: #334155; width: 140px; flex-shrink: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.method-bar-container { flex: 1; height: 6px; background: #f1f5f9; border-radius: 3px; overflow: hidden; }
.method-bar { height: 100%; background: #3b82f6; border-radius: 3px; transition: width 0.3s; }
.method-count { font-size: 0.6875rem; color: #64748b; font-weight: 600; width: 24px; text-align: right; flex-shrink: 0; }

.loading-state { padding: 24px; text-align: center; color: #94a3b8; font-size: 0.8125rem; }

.user-list { display: flex; flex-direction: column; gap: 4px; }
.user-row { display: flex; justify-content: space-between; align-items: center; padding: 8px 0; border-bottom: 1px solid #f8fafc; }
.user-info { display: flex; align-items: center; gap: 8px; }
.user-name { font-size: 0.8125rem; color: #334155; font-weight: 500; }

.user-badges { display: flex; gap: 6px; }
.reg-badge { font-size: 0.625rem; font-weight: 600; padding: 2px 8px; border-radius: 4px; text-transform: uppercase; }
.reg-ok { background: #dcfce7; color: #166534; }
.reg-no { background: #fee2e2; color: #991b1b; }
.reg-default { background: #f1f5f9; color: #64748b; }

.panel-subtitle { font-size: 0.6875rem; color: #94a3b8; margin: -8px 0 10px 0; }
.state-badge { font-size: 0.625rem; font-weight: 600; padding: 2px 8px; border-radius: 4px; text-transform: capitalize; flex-shrink: 0; }
.state-on { background: #dcfce7; color: #166534; }
.state-off { background: #f1f5f9; color: #94a3b8; }
.section-title { font-size: 0.9375rem; font-weight: 600; color: #1e293b; margin: 24px 0 10px 0; }

@media (max-width: 768px) {
  .panels-row { grid-template-columns: 1fr; }
}
</style>
